import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axiosConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Video, ArrowLeft, FileText, FlaskConical, Image as ImageIcon, FileIcon } from 'lucide-react';
import SharedTimer from '../components/SharedTimer';
import LiveKitCall from '../components/LiveKitCall';
import PdfViewer from '../components/PdfViewer';

const API = '/api';

const SessaoPage = () => {
  const { sessaoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessao, setSessao] = useState(null);
  const [estacao, setEstacao] = useState(null);
  const [roteiroData, setRoteiroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roteiroLoading, setRoteiroLoading] = useState(false);
  const [componentError, setComponentError] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100); // Zoom percentage
  const [userPapel, setUserPapel] = useState(null); // 'medico' or 'ator'
  
  // Shared tab state
  const [activeTab, setActiveTab] = useState('roteiro'); // 'roteiro', 'exames', 'checklist'
  
  // Ator-specific states
  const [exames, setExames] = useState([]);
  const [examesLoading, setExamesLoading] = useState(false);
  const [exameSelecionado, setExameSelecionado] = useState(null);
  const [exameErro, setExameErro] = useState(null);
  const [exameLoading, setExameLoading] = useState(false);
  const [sendingExame, setSendingExame] = useState(false);
  const [sentExamId, setSentExamId] = useState(null);
  
  // Medico-specific states  
  const [currentExam, setCurrentExam] = useState(null);
  const [currentExamUrl, setCurrentExamUrl] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Checklist states (FASE 3)
  const [checklistData, setChecklistData] = useState(null); // { items, responses, totalScore, maxScore, finalized }
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState(null);
  const [savingResponse, setSavingResponse] = useState(false);

  // Zoom handlers
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200)); // Max 200%
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50)); // Min 50%
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  // Global error handler for this component
  useEffect(() => {
    const handleError = (event) => {
      console.error('Error in SessaoPage:', event.error);
      setComponentError(true);
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  useEffect(() => {
    fetchSessaoData();
  }, [sessaoId]);

  // [EXAME ENVIO] Load exames when medico opens exames tab or page loads
  useEffect(() => {
    if (userPapel === 'medico' && sessaoId) {
      console.log('[EXAME ENVIO] Médico loading exames on mount/tab change, activeTab:', activeTab);
      fetchExames();
    }
  }, [userPapel, sessaoId]);

  // [EXAME ENVIO] Listen for WebSocket events - exame_enviado
  useEffect(() => {
    const handleExameEnviado = (event) => {
      console.log('[EXAME ENVIO] Received exame_enviado event:', event.detail);
      const { sessaoId: eventSessaoId } = event.detail;
      
      // Only process if it's for this session and user is medico
      if (eventSessaoId === sessaoId && userPapel === 'medico') {
        console.log('[EXAME ENVIO] Médico received exam, reloading list');
        
        // Show toast
        toast.success('Novo exame recebido do ator!');
        
        // Reload the full list from backend (ensures consistency)
        fetchExames();
      }
    };

    window.addEventListener('exame_enviado_ws', handleExameEnviado);
    
    return () => {
      window.removeEventListener('exame_enviado_ws', handleExameEnviado);
    };
  }, [sessaoId, userPapel]);

  const fetchSessaoData = async () => {
    try {
      // Fetch sessao first
      const sessaoResponse = await axios.get(`${API}/sessoes/${sessaoId}`);
      setSessao(sessaoResponse.data);
      const session = sessaoResponse.data;
      
      // CRITICAL FIX: Get papel from SESSION (papelCriador or papelConvidado)
      // NOT from user profile, because user can choose different papel for each session
      let papel = null;
      if (session.criadorId === user.id) {
        papel = session.papelCriador;
      } else if (session.convidadoId === user.id) {
        papel = session.papelConvidado;
      }
      
      setUserPapel(papel);

      // Fetch estacao
      const estacaoResponse = await axios.get(`${API}/estacoes/${session.estacaoId}`);
      setEstacao(estacaoResponse.data);

      // Fetch roteiro PDF with signed URL based on user papel
      setRoteiroLoading(true);
      try {
        // CRITICAL: Pass sessaoId to get roteiro for THIS specific session
        const roteiroResponse = await axios.get(`${API}/estacoes/${session.estacaoId}/roteiro?sessaoId=${session.id}`);
        const { disponivel, url } = roteiroResponse.data;
        
        // CRITICAL FIX: Backend returns correct URL based on papel
        // Store the response with the URL from backend
        setRoteiroData({
          ...roteiroResponse.data,
          urlFinal: url
        });
      } catch (error) {
        console.error('[SESSAO ROTEIRO] Error loading roteiro:', error);
        setRoteiroData({ disponivel: false, message: 'Roteiro não disponível' });
      } finally {
        setRoteiroLoading(false);
      }

      // [EXAME ENVIO] Fetch exames for both ator and medico (filtered by backend)
      if (papel === 'ator' || papel === 'medico') {
        // Call fetchExames after papel is set
        // Wait a bit for state to update
        setTimeout(() => fetchExames(), 100);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados da sessão');
      console.error('[SESSION] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // [EXAME ENVIO] Fetch exames filtered by role (ator sees all, medico sees only sent)
  const fetchExames = async () => {
    if (!sessaoId) {
      console.log('[SESSAO EXAMES] Skipping fetch - no sessaoId');
      return;
    }
    
    setExamesLoading(true);
    try {
      console.log('[SESSAO EXAMES] Fetching exames for sessao:', sessaoId, 'papel:', userPapel);
      const response = await axios.get(`${API}/sessoes/${sessaoId}/exames`);
      console.log('[SESSAO EXAMES] Loaded exames:', response.data.length);
      console.log('[SESSAO EXAMES] Exames:', response.data);
      setExames(response.data);
    } catch (error) {
      console.error('[SESSAO EXAMES] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        sessaoId
      });
      toast.error('Erro ao carregar exames');
    } finally {
      setExamesLoading(false);
    }
  };

  async function handleSelecionarExame(exame) {
    setExameLoading(true);
    setExameErro(null);
    setZoomLevel(100);

    console.log('[DEBUG exame clicado]', exame);

    // Se fileUrl é uma URL pública (começa com http), usar diretamente
    if (exame.fileUrl && exame.fileUrl.startsWith('http')) {
      setExameSelecionado({
        ...exame,
        url: exame.fileUrl,
      });
      setExameLoading(false);
      return;
    }

    // Se não, precisa buscar signed URL do backend
    try {
      const response = await axios.get(`${API}/sessoes/${sessaoId}/exames/${exame.id}`);
      const data = response.data;

      const url = data.url || data.fileUrl || data.signedUrl || data.downloadUrl;

      if (!url) {
        console.error('[DEBUG] Resposta sem URL', data);
        setExameErro('Exame sem arquivo associado');
        setExameSelecionado(null);
      } else {
        setExameSelecionado({
          ...exame,
          url,
        });
      }
    } catch (error) {
      console.error('[DEBUG] Erro ao buscar URL do exame', error);
      setExameErro('Erro ao carregar exame');
      setExameSelecionado(null);
    } finally {
      setExameLoading(false);
    }
  }

  // [EXAME ENVIO] ATOR: Send exam to medico
  const handleSendExamToMedico = async (exameId) => {
    setSendingExame(true);
    try {
      console.log(`[ATOR EXAMES] send sessaoId=${sessaoId} exameId=${exameId}`);
      const response = await axios.post(`${API}/sessoes/${sessaoId}/exames/${exameId}/enviar-ao-medico`);
      console.log(`[ATOR EXAMES] send sessaoId=${sessaoId} exameId=${exameId} status=${response.status}`);
      toast.success('Exame enviado ao médico');
      setSentExamId(exameId);
      
      // [EXAME ENVIO] Update local state to mark exam as sent
      setExames(prevExames => 
        prevExames.map(exame => 
          exame.id === exameId ? { ...exame, enviado_ao_medico: true } : exame
        )
      );
    } catch (error) {
      console.error(`[ATOR EXAMES] send sessaoId=${sessaoId} exameId=${exameId} status=${error.response?.status} error='${error.response?.data?.detail || error.message}'`);
      toast.error('Erro ao enviar exame');
    } finally {
      setSendingExame(false);
    }
  };

  // MEDICO: Clear exam selection
  const handleClearExam = async () => {
    try {
      console.log('[MEDICO EXAMES] Clearing exam, sessaoId:', sessaoId);
      await axios.delete(`${API}/sessoes/${sessaoId}/exam-selection`);
      console.log('[MEDICO EXAMES] Exam cleared');
      setCurrentExam(null);
      setCurrentExamUrl(null);
      toast.success('Exame limpo');
    } catch (error) {
      console.error('[MEDICO EXAMES] Error clearing exam:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        sessaoId
      });
      toast.error('Erro ao limpar exame');
    }
  };

  // MEDICO: Poll for currentExam
  useEffect(() => {
    if (userPapel === 'medico' && sessaoId) {
      console.log('[MEDICO EXAMES] Starting polling for currentExam');
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/sessoes/${sessaoId}`);
          const newCurrentExam = response.data.currentExam;
          
          if (newCurrentExam && newCurrentExam.id !== currentExam?.id) {
            console.log(`[MEDICO EXAMES] currentExam.id=${newCurrentExam.id} url=fetching status=loading`);
            setCurrentExam(newCurrentExam);
            // Fetch URL for the exam
            const urlResponse = await axios.get(`${API}/exames/${newCurrentExam.id}/url`);
            console.log(`[MEDICO EXAMES] currentExam.id=${newCurrentExam.id} url=${urlResponse.data.url.substring(0, 50)}... status=loaded`);
            setCurrentExamUrl(urlResponse.data.url);
          } else if (!newCurrentExam && currentExam) {
            console.log('[MEDICO EXAMES] currentExam.id=null url=empty status=empty');
            setCurrentExam(null);
            setCurrentExamUrl(null);
          }
        } catch (error) {
          console.error(`[MEDICO EXAMES] currentExam.id=error url=error status=error message='${error.message}'`);
        }
      }, 3000);
      
      setPollingInterval(interval);
      return () => {
        console.log('[MEDICO EXAMES] Stopping polling');
        clearInterval(interval);
      };
    }
  }, [userPapel, sessaoId, currentExam]);

  // ========== CHECKLIST FUNCTIONS (FASE 3) ==========
  
  // Helper to format description with line breaks, bold and italic
  const formatDescription = (text) => {
    if (!text) return '';
    
    // Escape HTML to prevent XSS
    let formatted = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
    
    // Convert **bold** to <strong>bold</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert _italic_ to <em>italic</em>
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Convert line breaks to <br>
    formatted = formatted.replace(/\n/g, '<br>');
    
    return formatted;
  };
  
  // Fetch checklist data when tab is opened
  useEffect(() => {
    if (activeTab === 'checklist' && sessaoId && !checklistData) {
      fetchChecklistData();
    }
  }, [activeTab, sessaoId]);

  const fetchChecklistData = async () => {
    setChecklistLoading(true);
    setChecklistError(null);
    try {
      console.log('[CHECKLIST] Fetching data for session:', sessaoId);
      const response = await axios.get(`${API}/checklist/session/${sessaoId}`);
      console.log('[CHECKLIST] Data loaded:', {
        items: response.data.items.length,
        responses: response.data.responses.length,
        totalScore: response.data.totalScore,
        maxScore: response.data.maxScore,
        finalized: response.data.finalized
      });
      setChecklistData(response.data);
    } catch (error) {
      console.error('[CHECKLIST] Error loading:', error.response?.data?.detail || error.message);
      if (error.response?.status === 404) {
        setChecklistError('empty'); // No checklist configured
      } else {
        setChecklistError('error');
        toast.error('Erro ao carregar checklist');
      }
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleChecklistOptionSelect = async (checklistItemId, selectedOption) => {
    if (checklistData?.finalized) {
      toast.error('Checklist já foi finalizado');
      return;
    }

    setSavingResponse(true);
    try {
      console.log('[CHECKLIST] Saving response:', { checklistItemId, selectedOption });
      await axios.post(`${API}/checklist/session/${sessaoId}/response`, {
        checklistItemId,
        selectedOption
      });
      
      // Refetch to get updated scores
      await fetchChecklistData();
      
      console.log('[CHECKLIST] Response saved successfully');
    } catch (error) {
      console.error('[CHECKLIST] Error saving response:', error.response?.data?.detail || error.message);
      toast.error(error.response?.data?.detail || 'Erro ao salvar resposta');
    } finally {
      setSavingResponse(false);
    }
  };

  const handleFinalizeChecklist = async () => {
    const confirmed = window.confirm(
      'Tem certeza que deseja finalizar o checklist?\n\nDepois não será possível editar as respostas.'
    );
    
    if (!confirmed) return;

    try {
      console.log('[CHECKLIST] Finalizing checklist');
      await axios.put(`${API}/checklist/session/${sessaoId}/finalize`);
      toast.success('Checklist finalizado com sucesso');
      
      // Update local state
      setChecklistData(prev => ({
        ...prev,
        finalized: true
      }));
      
      console.log('[CHECKLIST] Finalized successfully');

      // NEW: Automatically finalize the session to register performance data
      try {
        console.log('[SESSAO] Finalizing session to register performance:', sessaoId);
        const finalizeResponse = await axios.put(`${API}/sessoes/${sessaoId}/finalizar`);
        console.log('[SESSAO] Session finalized successfully:', finalizeResponse.data);
        toast.success(`Sessão finalizada! Pontuação: ${finalizeResponse.data.pontuacaoTotal}/${finalizeResponse.data.pontuacaoMaxima}`);
      } catch (sessionError) {
        console.error('[SESSAO] Error finalizing session:', sessionError.response?.data?.detail || sessionError.message);
        // Don't show error to user if session was already finalized
        if (sessionError.response?.status !== 404 && !sessionError.response?.data?.detail?.includes('já estava')) {
          toast.error('Checklist finalizado, mas houve erro ao registrar desempenho');
        }
      }
      
    } catch (error) {
      console.error('[CHECKLIST] Error finalizing:', error.response?.data?.detail || error.message);
      toast.error(error.response?.data?.detail || 'Erro ao finalizar checklist');
    }
  };

  const handleVoltar = () => {
    navigate('/usuario');
  };

  // [EXAME ENVIO] Render Exames tab for MEDICO (shows list of sent exames + viewer)
  const renderMedicoExamesTab = () => (
    <div className="h-[750px] flex gap-3">
      {/* Left: Exam List */}
      <div className="w-1/3 overflow-auto">
        {examesLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : exames.length === 0 ? (
          <div className="flex items-center justify-center h-full border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center px-4">
              <FlaskConical className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium mb-2">Aguardando exame do ator</p>
              <p className="text-gray-500 text-sm">
                O ator enviará um exame para você visualizar
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Exames Disponíveis</h3>
            <div className="space-y-2">
              {exames.map((exame) => (
                <div
                  key={exame.id}
                  onClick={() => handleSelecionarExame(exame)}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    exameSelecionado?.id === exame.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exame.tipo === 'image' ? (
                      <ImageIcon className="w-5 h-5 text-gray-600" />
                    ) : (
                      <FileIcon className="w-5 h-5 text-gray-600" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{exame.nome}</p>
                      <p className="text-xs text-gray-600">
                        {exame.tipo === 'image' ? 'Imagem' : 'PDF'}
                      </p>
                    </div>
                    <div className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      ✓
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: Exam Viewer */}
      <div className="flex-1 border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
        {exameLoading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Carregando exame...</p>
          </div>
        ) : exameErro ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-600">{exameErro}</p>
          </div>
        ) : !exameSelecionado ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-4">
              <FlaskConical className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Selecione um exame para visualizar</p>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-auto">
            <div
              style={{
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: 'top left',
                width: `${(100 / zoomLevel) * 100}%`,
                height: `${(100 / zoomLevel) * 100}%`,
              }}
            >
              {exameSelecionado.tipo === 'image' || exameSelecionado.tipo === 'Imagem' ? (
                <img
                  src={exameSelecionado.url}
                  alt={exameSelecionado.nome || 'Exame'}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <PdfViewer
                  key={exameSelecionado.id}
                  url={exameSelecionado.url}
                  title={exameSelecionado.nome || 'Exame'}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Checklist tab (FASE 3 - COMPLETE)
  const renderChecklistTab = () => {
    // Loading state
    if (checklistLoading) {
      return (
        <div className="h-[750px] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <p className="text-gray-600">Carregando checklist...</p>
          </div>
        </div>
      );
    }

    // Error state - no checklist configured
    if (checklistError === 'empty' || (checklistData && checklistData.items.length === 0)) {
      return (
        <div className="h-[750px] flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center px-4">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-700 font-medium mb-2">Nenhum checklist configurado</p>
            <p className="text-gray-500 text-sm">
              O administrador ainda não criou um checklist para esta estação.
            </p>
          </div>
        </div>
      );
    }

    // Error state - generic error
    if (checklistError === 'error') {
      return (
        <div className="h-[750px] flex items-center justify-center border-2 border-dashed border-red-200 rounded-lg bg-red-50">
          <div className="text-center px-4">
            <p className="text-red-700 font-medium mb-2">Erro ao carregar checklist</p>
            <p className="text-red-600 text-sm mb-4">
              Não foi possível carregar o checklist desta estação.
            </p>
            <Button
              onClick={fetchChecklistData}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      );
    }

    if (!checklistData) {
      return null;
    }

    // Helper to get selected option for an item
    const getSelectedOption = (itemId) => {
      const response = checklistData.responses.find(r => r.checklistItemId === itemId);
      return response?.selectedOption || null;
    };

    // Main checklist content
    return (
      <div className="h-[750px] overflow-y-auto px-4 py-6">
        {/* Header with score */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Checklist de Avaliação
              </h3>
              <p className="text-sm text-gray-600">
                Selecione uma opção para cada item abaixo
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">
                {checklistData.totalScore.toFixed(2)} / {checklistData.maxScore.toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">pontos</div>
            </div>
          </div>
        </div>

        {/* Finalized warning or finalize button */}
        {checklistData.finalized ? (
          <div className="mb-6 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center gap-2 text-yellow-800">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Checklist finalizado</span>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              Este checklist foi finalizado e não pode mais ser editado.
            </p>
          </div>
        ) : (
          <div className="mb-6 flex justify-end">
            <Button
              onClick={handleFinalizeChecklist}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={checklistData.responses.length === 0}
            >
              Finalizar Checklist
            </Button>
          </div>
        )}

        {/* Checklist items */}
        <div className="space-y-6">
          {checklistData.items.map((item, index) => {
            const selectedOption = getSelectedOption(item.id);
            
            return (
              <div
                key={item.id}
                className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm"
              >
                {/* Item header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {item.order}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">
                      {item.title}
                    </h4>
                    {item.description && (
                      <p 
                        className="text-sm text-gray-600 mb-2"
                        dangerouslySetInnerHTML={{ __html: formatDescription(item.description) }}
                      />
                    )}
                    {item.bullets && item.bullets.length > 0 && (
                      <ul className="text-sm text-gray-700 space-y-1 ml-4 mt-2">
                        {item.bullets.map((bullet, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span 
                              dangerouslySetInnerHTML={{ __html: formatDescription(bullet) }}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Options */}
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {/* Adequado */}
                  <button
                    onClick={() => handleChecklistOptionSelect(item.id, 'adequate')}
                    disabled={checklistData.finalized || savingResponse}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedOption === 'adequate'
                        ? 'border-green-500 bg-green-50 shadow-md'
                        : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                    } ${
                      checklistData.finalized || savingResponse
                        ? 'opacity-60 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedOption === 'adequate'
                            ? 'border-green-600 bg-green-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedOption === 'adequate' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={`font-medium ${
                        selectedOption === 'adequate' ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        Adequado
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      +{item.scoreAdequate} pts
                    </div>
                  </button>

                  {/* Parcialmente Adequado */}
                  <button
                    onClick={() => handleChecklistOptionSelect(item.id, 'partial')}
                    disabled={checklistData.finalized || savingResponse}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedOption === 'partial'
                        ? 'border-yellow-500 bg-yellow-50 shadow-md'
                        : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                    } ${
                      checklistData.finalized || savingResponse
                        ? 'opacity-60 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedOption === 'partial'
                            ? 'border-yellow-600 bg-yellow-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedOption === 'partial' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={`font-medium ${
                        selectedOption === 'partial' ? 'text-yellow-700' : 'text-gray-700'
                      }`}>
                        Parcial
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      +{item.scorePartial} pts
                    </div>
                  </button>

                  {/* Inadequado */}
                  <button
                    onClick={() => handleChecklistOptionSelect(item.id, 'inadequate')}
                    disabled={checklistData.finalized || savingResponse}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedOption === 'inadequate'
                        ? 'border-red-500 bg-red-50 shadow-md'
                        : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                    } ${
                      checklistData.finalized || savingResponse
                        ? 'opacity-60 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedOption === 'inadequate'
                            ? 'border-red-600 bg-red-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedOption === 'inadequate' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={`font-medium ${
                        selectedOption === 'inadequate' ? 'text-red-700' : 'text-gray-700'
                      }`}>
                        Inadequado
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      +{item.scoreInadequate} pts
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom spacer */}
        <div className="h-8"></div>
      </div>
    );
  };

  // Show error UI instead of red overlay
  if (componentError) {
    return (
      <div className="min-h-screen bg-[#191919] text-white flex items-center justify-center p-4">
        <div className="max-w-lg text-center bg-gray-800 rounded-lg p-8 border border-gray-700">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-3 text-white">Erro ao Carregar Sessão</h2>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Ocorreu um erro ao tentar carregar a página da sessão. Por favor, tente recarregar a página ou entre em contato com o suporte.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#CF0D1D] hover:bg-[#A00A17] text-white rounded-lg font-semibold transition-all"
            >
              Recarregar Página
            </button>
            <button
              onClick={() => navigate('/usuario')}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
            >
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#191919]">
        <div className="text-white text-xl flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          Carregando sessão...
        </div>
      </div>
    );
  }

  // Render Roteiro tab content (for both medico and ator)
  const renderRoteiroTab = () => (
    <>
      {roteiroLoading ? (
        <div className="flex items-center justify-center h-[600px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Carregando roteiro...</p>
          </div>
        </div>
      ) : roteiroData?.disponivel && roteiroData?.urlFinal ? (
        <div className="space-y-3">
          {/* PDF viewer: scroll vertical, largura responsiva, sem cortar conteúdo */}
          <div
            className="w-full border border-gray-300 rounded-lg shadow-lg bg-gray-50"
            style={{
              maxHeight: '75vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: 8,
            }}
          >
            <PdfViewer url={roteiroData.urlFinal} title="Roteiro PDF" />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
            <p className="text-xs text-blue-800">
              <strong>📌 Dica:</strong> Leia o roteiro com atenção. O PDF permanece visível durante toda a sessão.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-[750px] border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center px-4">
            <p className="text-gray-600 font-medium mb-2">
              Roteiro ainda não cadastrado para este papel
            </p>
            <p className="text-gray-500 text-sm">
              {roteiroData?.message || 'Entre em contato com o administrador'}
            </p>
          </div>
        </div>
      )}
    </>
  );

  // Render Exames tab content for ATOR
  const renderAtorExamesTab = () => (
    <div className="h-[750px] flex gap-4">
      {/* Lista de exames - lado esquerdo */}
      <div className="w-64 border-r border-gray-300 overflow-y-auto pr-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Exames Disponíveis</h3>
        {examesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : exames.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            Nenhum exame disponível para esta estação
          </p>
        ) : (
          <div className="space-y-2">
            {exames.map((exame) => (
              <div
                key={exame.id}
                className={`rounded-lg border transition-all ${
                  sentExamId === exame.id
                    ? 'bg-green-50 border-green-300'
                    : exameSelecionado?.id === exame.id
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-white border-gray-200'
                }`}
              >
                <button
                  onClick={() => handleSelecionarExame(exame)}
                  className="w-full text-left px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    {exame.tipo === 'image' ? (
                      <ImageIcon className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <FileIcon className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium truncate">{exame.nome}</span>
                  </div>
                  <span className="text-xs text-gray-500 ml-6">
                    {exame.tipo === 'image' ? 'Imagem' : 'PDF'}
                  </span>
                </button>
                <div className="px-3 pb-2">
                  {sentExamId === exame.id ? (
                    <div className="text-xs text-green-600 font-medium">
                      ✓ Enviado ao médico
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSendExamToMedico(exame.id)}
                      disabled={sendingExame}
                      className="w-full px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingExame ? '📤 Enviando...' : '📤 Enviar ao Médico'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visualizador de exame - lado direito */}
      <div className="flex-1 overflow-auto">
        {exameLoading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Carregando exame...</p>
          </div>
        ) : exameErro ? (
          <div className="flex items-center justify-center h-full border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-600">{exameErro}</p>
          </div>
        ) : !exameSelecionado ? (
          <div className="flex items-center justify-center h-full border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center px-4">
              <FlaskConical className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium mb-1">Selecione um exame</p>
              <p className="text-gray-500 text-sm">
                Clique em um exame da lista para visualizá-lo
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full">
            {exameSelecionado.tipo === 'image' || exameSelecionado.tipo === 'Imagem' ? (
              <div className="h-full border border-gray-300 rounded-lg shadow-lg overflow-auto bg-gray-50">
                <div
                  style={{
                    transform: `scale(${zoomLevel / 100})`,
                    transformOrigin: 'top left',
                    width: `${(100 / zoomLevel) * 100}%`,
                    height: `${(100 / zoomLevel) * 100}%`,
                    minHeight: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={exameSelecionado.url}
                    alt={exameSelecionado.nome || 'Exame'}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
              </div>
            ) : (
              <div
                className="border border-gray-300 rounded-lg shadow-lg bg-gray-50"
                style={{ maxHeight: '75vh', overflowY: 'auto', overflowX: 'hidden', padding: 8 }}
              >
                <PdfViewer
                  key={exameSelecionado.id}
                  url={exameSelecionado.url}
                  title={exameSelecionado.nome || 'Exame'}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#191919] text-white">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Button
            onClick={handleVoltar}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-2"
            data-testid="voltar-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          {userPapel !== 'medico' && (
            <div>
              <h1 className="text-2xl font-bold">{estacao?.nome}</h1>
              <p className="text-gray-400 text-sm mt-1">{estacao?.descricaoCurta}</p>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Video Area */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="bg-[#0f0f0f] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Video className="w-5 h-5 text-[#CF0D1D]" />
                  Chamada de Vídeo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 sm:p-6">
                <div className="w-full bg-black rounded-lg overflow-hidden" style={{ minHeight: '280px', maxHeight: '320px' }}>
                  {sessao && userPapel ? (
                    <LiveKitCall 
                      roomName={`sessao-${sessao.id}`}
                      role={userPapel}
                      sessaoId={sessao.id}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full border-2 border-dashed border-gray-700">
                      <Loader2 className="w-12 h-12 text-[#CF0D1D] animate-spin mb-4" />
                      <p className="text-xl font-semibold text-white mb-2">Carregando...</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* NEW: Shared Timer (Cronômetro Compartilhado) */}
            {sessao && estacao && (
              <SharedTimer 
                sessaoId={sessaoId} 
                duracaoMinutos={estacao.duracao_minutos}
              />
            )}
          </div>

          {/* Roteiro/Exames Area - BIGGER */}
          <div className="lg:col-span-8">
            <Card className="bg-white border-gray-200 h-full min-h-[700px]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Tabs for Ator and Medico */}
                    {userPapel === 'ator' || userPapel === 'medico' ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setActiveTab('roteiro')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            activeTab === 'roteiro'
                              ? 'bg-blue-100 text-blue-800'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <FileText className="w-4 h-4" />
                          Roteiro
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('exames');
                            setZoomLevel(100);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            activeTab === 'exames'
                              ? 'bg-blue-100 text-blue-800'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <FlaskConical className="w-4 h-4" />
                          Exames
                          {userPapel === 'ator' && exames.length > 0 && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {exames.length}
                            </span>
                          )}
                          {userPapel === 'medico' && currentExam && (
                            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                              1
                            </span>
                          )}
                        </button>
                        {/* Checklist only for ATOR - NEVER show for medico */}
                        {userPapel === 'ator' && (
                          <button
                            onClick={() => setActiveTab('checklist')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                              activeTab === 'checklist'
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Checklist
                          </button>
                        )}
                      </div>
                    ) : (
                      <CardTitle className="text-[#191919]">Seu Roteiro</CardTitle>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Zoom Controls - show when viewing content */}
                    {((activeTab === 'roteiro' && roteiroData?.disponivel) || 
                      (activeTab === 'exames' && exameSelecionado)) && (
                      <div className="flex items-center gap-1 border border-gray-300 rounded-md overflow-hidden">
                        <button
                          onClick={handleZoomOut}
                          className="px-3 py-1 hover:bg-gray-100 transition-colors text-gray-700 font-semibold border-r border-gray-300"
                          title="Diminuir zoom"
                        >
                          🔍−
                        </button>
                        <button
                          onClick={handleResetZoom}
                          className="px-3 py-1 hover:bg-gray-100 transition-colors text-gray-700 text-xs font-medium border-r border-gray-300"
                          title="Resetar zoom"
                        >
                          {zoomLevel}%
                        </button>
                        <button
                          onClick={handleZoomIn}
                          className="px-3 py-1 hover:bg-gray-100 transition-colors text-gray-700 font-semibold"
                          title="Aumentar zoom"
                        >
                          🔍+
                        </button>
                      </div>
                    )}
                    {roteiroData?.papel && activeTab === 'roteiro' && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        roteiroData.papel === 'medico' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {roteiroData.papel === 'medico' ? '⚕️ Médico' : '🎭 Ator'}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-2 lg:p-3">
                {activeTab === 'roteiro' && renderRoteiroTab()}
                {activeTab === 'exames' && (userPapel === 'ator' ? renderAtorExamesTab() : renderMedicoExamesTab())}
                {activeTab === 'checklist' && renderChecklistTab()}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Session Info */}
        <div className="mt-6">
          <Card className="bg-[#0f0f0f] border-gray-800">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Status da Sessão</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 rounded-full">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <p className="text-yellow-500 font-semibold text-sm uppercase">
                      {sessao?.status.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Tempo Máximo</p>
                  <p className="text-white font-semibold text-lg">{estacao?.tempoMaximoMinutos} minutos</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Criada em</p>
                  <p className="text-white font-semibold text-lg">
                    {new Date(sessao?.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SessaoPage;
